import React from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';
import { cn } from '../ui/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'utility' | 'ghost';
export type ButtonState = 'default' | 'loading' | 'disabled';
export type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'size' | 'children'> {
  variant?: ButtonVariant;
  state?: ButtonState;
  size?: ButtonSize;
  fullWidth?: boolean;
  loadingText?: string;
  children: React.ReactNode;
}

export function Button({ 
  variant = 'primary', 
  state = 'default',
  size = 'medium',
  fullWidth = false,
  loadingText,
  children,
  className = '',
  disabled,
  ...props 
}: ButtonProps) {
  const isDisabled = disabled || state === 'disabled';
  const isLoading = state === 'loading';

  const baseStyles = `
    relative
    rounded-[var(--radius-md)]
    font-medium
    transition-colors duration-200
    touch-manipulation
    flex items-center justify-center gap-2
    ${fullWidth ? 'w-full' : ''}
  `;

  const sizeStyles = {
    small: 'px-4 py-2 min-h-[36px] text-sm',
    medium: 'px-6 py-3 min-h-[48px]',
    large: 'px-8 py-4 min-h-[56px]',
  };

  const variantStyles = {
    primary: `
      bg-[var(--color-primary)]
      text-[var(--color-primary-foreground)]
      shadow-[var(--elevation-sm)]
      hover:shadow-[var(--elevation-md)]
    `,
    secondary: `
      bg-[var(--color-secondary)]
      text-[var(--color-primary-foreground)]
      shadow-[var(--elevation-sm)]
      hover:shadow-[var(--elevation-md)]
    `,
    utility: `
      bg-transparent
      text-[var(--text-primary)]
      border border-[var(--border)]
      hover:bg-[var(--surface)]
      hover:border-[var(--text-tertiary)]
    `,
    ghost: `
      bg-transparent
      text-[var(--text-secondary)]
      hover:bg-[var(--surface)]
      hover:text-[var(--text-primary)]
    `,
  };

  const disabledStyles = `
    bg-[var(--color-primary-disabled)]
    text-[var(--text-inverse)]
    border border-[var(--color-primary-disabled)]
    cursor-not-allowed
    opacity-50
    shadow-none
    hover:shadow-none
    pointer-events-none
  `;

  const loadingStyles = `
    cursor-wait
    pointer-events-none
  `;

  return (
    <motion.button
      whileHover={isDisabled ? undefined : { y: -2, scale: 1.01 }}
      whileTap={isDisabled ? undefined : { y: 0, scale: 0.98 }}
      className={cn(
        baseStyles,
        sizeStyles[size],
        isDisabled ? disabledStyles : variantStyles[variant],
        isLoading ? loadingStyles : '',
        className
      )}
      disabled={isDisabled}
      {...props}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center gap-2">
          <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {loadingText ? <span>{loadingText}</span> : null}
        </span>
      )}
      <span className={cn("flex items-center justify-center gap-2 py-1", isLoading ? 'opacity-0' : '')}>
        {children}
      </span>
    </motion.button>
  );
}